import { Code, ConnectError } from "@connectrpc/connect";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef } from "react";

import { useGitpodAPI } from "@/hooks/use-gitpod-api";
import { handleNotificationEvent } from "@/hooks/use-notifications";
import { handleAgentExecutionEvent } from "@/queries/agent-queries";
import { handleWorkflowEvents } from "@/queries/automation-queries";
import { handleAutomationEvents } from "@/queries/automations-queries";
import { handleEnvironmentClassEvent, handleEnvironmentEvent } from "@/queries/environment-queries";
import { handleGroupMembershipEvent, handleRoleAssignmentEvent } from "@/queries/group-queries";
import { handleOrganizationPolicyEvent } from "@/queries/organization-queries";
import { handlePrebuildEvent } from "@/queries/prebuild-queries";
import { handleProjectEvent } from "@/queries/project-queries";
import { handlePromptEvent } from "@/queries/prompt-queries";
import { handleHostAuthenticationTokenEvent } from "@/queries/runner-configuration-queries";
import { handleRunnerEvent } from "@/queries/runner-queries";
import { handleProjectSecretEvent } from "@/queries/secret-queries";
import { handleUserSecretEvent } from "@/queries/secret-queries";
import { handleServiceAccountEvent } from "@/queries/service-account-queries";
import { handleUserPreferenceEvent } from "@/queries/user-preferences-queries";
import { useAuthenticatedUser } from "@/queries/user-queries";
import { handleWarmPoolEvent } from "@/queries/warm-pool-queries";
import { reconnectDelay } from "@/utils/reconnect-backoff";
import { ResourceOperation, ResourceType } from "gitpod-next-api/gitpod/v1/event_pb";

// Debounce window for coalescing rapid UPDATE events (e.g. creating → starting → running).
// Per-environment streams use a shorter window to keep the active environment's UI responsive.
// Org-wide streams use a longer window because they observe all environments and bursts overlap.
export const DEBOUNCE_DELAY_ENV_MS = 100;
export const DEBOUNCE_DELAY_ORG_MS = 500;
// Cap the maximum delay so chatty environments can't starve the fetch indefinitely.
export const MAX_WAIT_MS = 2000;

type Scope =
    | {
          case: "environmentId";
          value: string;
      }
    | {
          case: "organization";
          value: true;
      };

/**
 * Legacy unfiltered event stream, used when the `scoped_event_streams_enabled`
 * flag is OFF (branching happens in `OrgEventStreamProvider.tsx`). The scoped
 * replacement lives in `dispatch-org-event.ts`. When adding a new ResourceType
 * handler here, also add it there (and vice versa) until this legacy path is
 * removed.
 */
export const useStreamEvents = (scope?: Scope) => {
    const api = useGitpodAPI();
    const { data: user } = useAuthenticatedUser();
    const client = useQueryClient();

    const abortControllerRef = useRef(new AbortController());
    useEffect(() => {
        if (!scope) {
            return;
        }

        if (!client) {
            console.log("no client, aborting stream events for", scope.case);
            return;
        }

        if (!user) {
            console.log("no user, aborting stream events for", scope.case);
            return;
        }
        if (scope.case === "environmentId" && !scope.value) {
            return;
        }

        const abortController = new AbortController();
        abortControllerRef.current = abortController;
        const scopeLabel = scope.case === "environmentId" ? `environment ${scope.value}` : "organization";
        const debounceDelayMs = scope.case === "environmentId" ? DEBOUNCE_DELAY_ENV_MS : DEBOUNCE_DELAY_ORG_MS;
        const maxWaitMs = MAX_WAIT_MS;
        const pendingDebounceTimers = new Map<string, ReturnType<typeof setTimeout>>();
        // Tracks when the first event in each burst arrived, used to enforce maxWait.
        const burstStartTimes = new Map<string, number>();

        runUntilAborted(abortController, async () => {
            console.debug(`[WatchEvents] Starting stream for ${scopeLabel}`);

            try {
                for await (const response of api.eventService.watchEvents(
                    {
                        scope,
                    },
                    {
                        signal: abortController.signal,
                        timeoutMs: -1, // disable default timeout
                    },
                )) {
                    if (abortController.signal.aborted) {
                        console.debug(`[WatchEvents] Stream aborted for ${scopeLabel}`);
                        return;
                    }

                    // Skip keepalive messages (empty responses sent to prevent proxy timeouts)
                    if (response.resourceType === ResourceType.UNSPECIFIED) {
                        continue;
                    }

                    console.debug(`[WatchEvents] Received event for ${scopeLabel}:`, {
                        resourceType: nameOfResourceType(response.resourceType),
                        operation: nameOfOperation(response.operation),
                        resourceId: response.resourceId,
                    });

                    switch (response.resourceType) {
                        case ResourceType.ENVIRONMENT:
                            if (response.operation === ResourceOperation.UPDATE) {
                                // Debounce rapid UPDATE events per environment ID.
                                // Rapid phase transitions (creating → starting → running) fire
                                // multiple events; only the trailing fetch matters.
                                const envId = response.resourceId;
                                const now = Date.now();
                                const existing = pendingDebounceTimers.get(envId);
                                if (existing !== undefined) {
                                    clearTimeout(existing);
                                }
                                if (!burstStartTimes.has(envId)) {
                                    burstStartTimes.set(envId, now);
                                }
                                // Clamp the delay so the total wait since the first event
                                // in this burst never exceeds maxWaitMs.
                                const elapsed = now - burstStartTimes.get(envId)!;
                                const delay = Math.max(0, Math.min(debounceDelayMs, maxWaitMs - elapsed));
                                pendingDebounceTimers.set(
                                    envId,
                                    setTimeout(() => {
                                        pendingDebounceTimers.delete(envId);
                                        burstStartTimes.delete(envId);
                                        handleEnvironmentEvent(api, client, response).catch((err) =>
                                            console.error(
                                                `[WatchEvents] Failed to handle debounced environment event for ${envId}:`,
                                                err,
                                            ),
                                        );
                                    }, delay),
                                );
                            } else {
                                await handleEnvironmentEvent(api, client, response);
                            }
                            break;
                        case ResourceType.RUNNER:
                            await handleRunnerEvent(api, client, response);
                            break;
                        case ResourceType.ENVIRONMENT_CLASS:
                            await handleEnvironmentClassEvent(client);
                            break;
                        case ResourceType.PROJECT:
                            await handleProjectEvent(api, client, response);
                            break;
                        case ResourceType.PREBUILD:
                            await handlePrebuildEvent(api, client, response);
                            break;
                        case ResourceType.WARM_POOL:
                            await handleWarmPoolEvent(client, response);
                            break;
                        case ResourceType.TASK:
                        case ResourceType.SERVICE:
                        case ResourceType.TASK_EXECUTION: {
                            const environmentId = scope.case === "environmentId" ? scope.value : undefined;
                            if (!environmentId) {
                                console.error("Received automation event outside of environment scope", response);
                                break;
                            }
                            await handleAutomationEvents(api, client, response, environmentId);
                            break;
                        }
                        case ResourceType.USER_PREFERENCE: {
                            await handleUserPreferenceEvent(api, client, response);
                            break;
                        }
                        case ResourceType.SECRET: {
                            await handleProjectSecretEvent(api, client, response);
                            break;
                        }
                        case ResourceType.USER_SECRET: {
                            await handleUserSecretEvent(api, client, response);
                            break;
                        }
                        case ResourceType.AGENT_EXECUTION: {
                            await handleAgentExecutionEvent(api, client, response);
                            break;
                        }
                        case ResourceType.ORGANIZATION_POLICY: {
                            const organizationId = scope.case === "organization" ? user?.organizationId : undefined;
                            if (!organizationId) {
                                console.error(
                                    "Received organization policy event outside of organization scope",
                                    response,
                                );
                                break;
                            }
                            await handleOrganizationPolicyEvent(api, client, response, organizationId);
                            break;
                        }
                        case ResourceType.PROMPT: {
                            handlePromptEvent(api, client, response);
                            break;
                        }
                        case ResourceType.WORKFLOW:
                        case ResourceType.WORKFLOW_EXECUTION:
                        case ResourceType.WORKFLOW_EXECUTION_ACTION: {
                            await handleWorkflowEvents(api, client, response);
                            break;
                        }
                        case ResourceType.ROLE_ASSIGNMENT_CHANGED: {
                            await handleRoleAssignmentEvent(client);
                            break;
                        }
                        case ResourceType.GROUP_MEMBERSHIP_CHANGED: {
                            await handleGroupMembershipEvent(client);
                            break;
                        }
                        case ResourceType.SERVICE_ACCOUNT: {
                            await handleServiceAccountEvent(api, client, response);
                            break;
                        }
                        case ResourceType.HOST_AUTHENTICATION_TOKEN: {
                            await handleHostAuthenticationTokenEvent(api, client, response);
                            break;
                        }
                        case ResourceType.NOTIFICATION: {
                            await handleNotificationEvent(api, client, response);
                            break;
                        }
                        default:
                            console.warn(
                                `[WatchEvents] Unhandled resource type for ${scopeLabel}:`,
                                response.resourceType,
                            );
                    }
                }
                console.debug(`[WatchEvents] Stream ended normally for ${scopeLabel}`);
            } catch (error) {
                // Check if this is an intentional cancellation
                if (error instanceof ConnectError && error.code === Code.Canceled) {
                    console.debug(`[WatchEvents] Stream canceled for ${scopeLabel}`);
                    return;
                }

                // Check if abort was triggered (might happen before the error is thrown)
                if (abortController.signal.aborted) {
                    console.debug(`[WatchEvents] Stream aborted during operation for ${scopeLabel}`);
                    return;
                }

                console.error(`[WatchEvents] Stream error for ${scopeLabel}:`, error);
                throw error;
            }
        }).catch((error) => {
            // Double-check for cancellation in the outer catch
            if (error instanceof ConnectError && error.code === Code.Canceled) {
                console.debug(`[WatchEvents] Stream cleanup after cancellation for ${scopeLabel}`);
                return;
            }

            if (abortController.signal.aborted) {
                console.debug(`[WatchEvents] Stream cleanup after abort for ${scopeLabel}`);
                return;
            }
            console.error(`[WatchEvents] Unhandled error for ${scopeLabel}:`, error);
        });

        return () => {
            const scopeLabel = scope.case === "environmentId" ? `environment ${scope.value}` : "organization";
            console.debug(`[WatchEvents] Cleanup triggered for ${scopeLabel}`);
            for (const timer of pendingDebounceTimers.values()) {
                clearTimeout(timer);
            }
            pendingDebounceTimers.clear();
            burstStartTimes.clear();
            abortController.abort(`Cleanup - ${scopeLabel}`);
        };
    }, [api, client, scope, user]);

    return {
        unsubscribe: () => {
            if (abortControllerRef.current) {
                abortControllerRef.current.abort("Manual unsubscribe");
            }
        },
    };
};

async function runUntilAborted(abortcontroller: AbortController, asyncFunction: () => Promise<void>) {
    let attempt = 0;
    while (!abortcontroller.signal.aborted) {
        try {
            await asyncFunction();
            attempt = 0;
        } catch (error) {
            if (error instanceof ConnectError) {
                // These errors should stop retrying
                if (
                    error.code === Code.Canceled ||
                    error.code === Code.NotFound ||
                    error.code === Code.PermissionDenied ||
                    error.code === Code.Unauthenticated ||
                    error.code === Code.Internal
                ) {
                    abortcontroller.abort(`Stream error: ${error.code}`);
                    return;
                }
            }
            if (abortcontroller.signal.aborted) {
                return;
            }
            const delay = reconnectDelay(attempt);
            console.debug(`[WatchEvents] Reconnecting in ${Math.round(delay)}ms (attempt ${attempt + 1})`);
            await new Promise((resolve) => setTimeout(resolve, delay));
            attempt++;
        }
    }
}

function nameOfResourceType(resourceType: ResourceType): string {
    return ResourceType[resourceType] ?? "UNKNOWN_RESOURCE_TYPE";
}

function nameOfOperation(operation: number): string {
    return ResourceOperation[operation] ?? "UNKNOWN_OPERATION";
}
